import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { ArrowLeft, Loader2, FileText, BarChart as ChartBar, Calculator, Pencil, Copy, Trash2, RefreshCw, Plus, Archive } from 'lucide-react';
import { TransactionSummary } from './TransactionSummary';
import { TransactionTable } from './TransactionTable';
import { GenerateDatasetMappingModal } from './GenerateDatasetMappingModal';
import { DeleteConfirmationModal } from '../common/DeleteConfirmationModal';
import type { TransactionWithFees } from './types';

interface SavedDataset {
  id: string;
  name: string;
  site_id: number;
  date_range: string;
  start_date: string;
  end_date: string;
  created_at: string;
}

interface Stats {
  totalTransactions: number;
  totalAmount: number;
  totalSiteFees: number;
  totalBankFees: number;
  totalResellerFees: number;
  approvedTransactions: number;
  declinedTransactions: number;
  returnedTransactions: number;
}

interface DatasetMappingItem {
  id: string;
  name: string;
  merchant: { id: string; name: string };
  bank_mappings: Record<string, { bank_id: string; bank_name: string; bank_alias: string }>;
  created_at: string;
  archived: boolean;
}

export function DatasetDetails() {
  const { datasetId } = useParams();
  const navigate = useNavigate();
  const [dataset, setDataset] = useState<SavedDataset | null>(null);
  const [stats, setStats] = useState<Stats | null>(null);
  const [transactions, setTransactions] = useState<TransactionWithFees[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    status: '',
    method: '',
    bank: ''
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showTransactions, setShowTransactions] = useState(false);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const ITEMS_PER_PAGE = 50;
  const [showGenerateQuote, setShowGenerateQuote] = useState(false);
  const [bankMappings, setBankMappings] = useState<Record<string, string>>({});
  const [totalTransactionCount, setTotalTransactionCount] = useState(0);

  const [datasetMappings, setDatasetMappings] = useState<DatasetMappingItem[]>([]);
  const [loadingMappings, setLoadingMappings] = useState(true);
  const [mappingToDelete, setMappingToDelete] = useState<DatasetMappingItem | null>(null);
  const [deletingMapping, setDeletingMapping] = useState(false);
  const [duplicatingMapping, setDuplicatingMapping] = useState<string | null>(null);
  const [archivingMapping, setArchivingMapping] = useState<string | null>(null);

  useEffect(() => {
    if (!datasetId) {
      navigate('/datasets');
      return;
    }
  }, [datasetId, navigate]);

  useEffect(() => {
    if (datasetId) {
      loadDatasetData();
      loadDatasetMappings();
    }
  }, [datasetId]);

  const loadDatasetData = async () => {
      try {
        const { data, error } = await supabase
          .rpc('get_dataset_details', { dataset_id_param: datasetId });

        if (error) throw error;
        if (!data) throw new Error('Dataset not found');

        const { data: bankData } = await supabase
          .from('dataset_transactions')
          .select('bank')
          .eq('dataset_id', datasetId)
          .limit(1000);

        const uniqueBanks = [...new Set(bankData?.map(t => t.bank.toLowerCase().trim()) || [])];
        setBankMappings(uniqueBanks.reduce((acc, bank) => ({
          ...acc,
          [bank]: ''
        }), {}));
        
        setDataset(data.dataset);
        setStats(data.stats);

      } catch (err) {
        console.error('Error loading dataset data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load dataset data');
      } finally {
        setLoading(false);
      }
  };

  const loadDatasetMappings = async () => {
    setLoadingMappings(true);
    try {
      const { data, error } = await supabase
        .from('dataset_mappings')
        .select(`
          id, name, created_at, archived,
          merchant:merchant_id ( id, name ),
          bank_mappings:dataset_mapping_bank_mappings (
            dataset_bank_name,
            bank:bank_id ( id, name, alias )
          )
        `)
        .eq('dataset_id', datasetId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const transformed = (data || []).map((m: any) => ({
        ...m,
        bank_mappings: (m.bank_mappings || []).reduce((acc: Record<string, any>, bm: any) => ({
          ...acc,
          [bm.dataset_bank_name]: {
            bank_id: bm.bank.id,
            bank_name: bm.bank.name,
            bank_alias: bm.bank.alias
          }
        }), {})
      }));
      setDatasetMappings(transformed);
    } catch (err) {
      console.error('Error loading dataset mappings:', err);
    } finally {
      setLoadingMappings(false);
    }
  };

  const handleDeleteMapping = async () => {
    if (!mappingToDelete) return;
    setDeletingMapping(true);
    try {
      const { error } = await supabase.rpc('delete_dataset_mapping', { mapping_id_param: mappingToDelete.id });
      if (error) throw error;
      await loadDatasetMappings();
      setMappingToDelete(null);
    } catch (err) {
      console.error('Error deleting mapping:', err);
    } finally {
      setDeletingMapping(false);
    }
  };

  const handleDuplicateMapping = async (mapping: DatasetMappingItem) => {
    setDuplicatingMapping(mapping.id);
    try {
      const { error } = await supabase.rpc('duplicate_dataset_mapping', { mapping_id_param: mapping.id, new_name: `${mapping.name} (Copy)` });
      if (error) throw error;
      await loadDatasetMappings();
    } catch (err) {
      console.error('Error duplicating mapping:', err);
    } finally {
      setDuplicatingMapping(null);
    }
  };

  const handleToggleArchiveMapping = async (mappingId: string, archive: boolean) => {
    setArchivingMapping(mappingId);
    try {
      const { error } = await supabase.rpc('toggle_dataset_mapping_archive', { mapping_id_param: mappingId, archive });
      if (error) throw error;
      await loadDatasetMappings();
    } catch (err) {
      console.error('Error toggling archive:', err);
    } finally {
      setArchivingMapping(null);
    }
  };

  const loadTransactions = async () => {
    if (!showTransactions) return;
    
    setLoadingTransactions(true);
    setError(null);
    
    try {
      const { count: totalCount } = await supabase
        .from('dataset_transactions')
        .select('*', { count: 'exact', head: true })
        .eq('dataset_id', datasetId);

      setTotalTransactionCount(totalCount || 0);

      const { data: transactions, error } = await supabase
        .from('dataset_transactions')
        .select('*')
        .eq('dataset_id', datasetId)
        .order('timestamp')
        .range(
          (currentPage - 1) * ITEMS_PER_PAGE,
          (currentPage * ITEMS_PER_PAGE) - 1
        );
      
      if (error) throw error;
      
      let filteredTransactions = transactions || [];
      if (filters.status) {
        filteredTransactions = filteredTransactions.filter(t => 
          t.status.toLowerCase() === filters.status.toLowerCase()
        );
      }
      if (filters.method) {
        filteredTransactions = filteredTransactions.filter(t => 
          t.method.toLowerCase() === filters.method.toLowerCase()
        );
      }
      if (filters.bank) {
        filteredTransactions = filteredTransactions.filter(t => 
          t.bank.toLowerCase() === filters.bank.toLowerCase()
        );
      }

      setTransactions(filteredTransactions);

    } catch (err) {
      console.error('Error loading transactions:', err);
      setError(err instanceof Error ? err.message : 'Failed to load transactions');
    } finally {
      setLoadingTransactions(false);
    }
  };

  useEffect(() => {
    if (showTransactions) {
      loadTransactions();
    }
  }, [showTransactions, currentPage, filters]);

  const totalPages = Math.ceil(totalTransactionCount / ITEMS_PER_PAGE);

  if (loading) {
    return (
      <div className="space-y-6">
        <Link to="/datasets" className="back-link">
          <ArrowLeft className="w-4 h-4" />
          Back to Datasets
        </Link>
        <div className="flex justify-center items-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </div>
    );
  }

  if (error && !dataset) {
    return (
      <div className="text-center py-12">
        <div className="card p-6 max-w-md mx-auto">
          <p className="text-[14px] text-red-600">{error || 'Dataset not found'}</p>
        </div>
      </div>
    );
  }

  if (!dataset) return null;

  return (
    <div className="space-y-6">
      <Link to="/datasets" className="back-link">
        <ArrowLeft className="w-4 h-4" />
        Back to Datasets
      </Link>

      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">{dataset.name}</h1>
          <p className="page-subtitle">
            Created {new Date(dataset.created_at).toLocaleDateString()} &middot;{' '}
            {dataset.date_range === '3days' ? 'Last 3 Days' : 'Last Month'}
          </p>
        </div>
        <Link to={`/datasets/${datasetId}/fees`} className="btn-primary">
          <ChartBar className="w-4 h-4" />
          View Fee Report
        </Link>
      </div>

      {/* Transaction Summary */}
      {!stats ? (
        <div className="card p-8 text-center">
          <p className="text-[14px] text-slate-500">No transactions found for this dataset.</p>
        </div>
      ) : (
        <>
          <div className="card">
            <TransactionSummary
              stats={stats}
              dateRange={dataset.date_range}
            />
          </div>

          <div className="card p-5">
            {!showTransactions ? (
              <button
                onClick={() => setShowTransactions(true)}
                className="btn-secondary"
              >
                <FileText className="w-4 h-4" />
                View Individual Transactions
              </button>
            ) : (
              loadingTransactions ? (
                <div className="flex justify-center items-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                </div>
              ) : (
                <TransactionTable
                  transactions={transactions}
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                  filters={filters}
                  onFilterChange={setFilters}
                />
              )
            )}
          </div>
        </>
      )}
      
      {/* Dataset Mappings Section */}
      <div className="card">
        <div className="p-5 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="section-title">Dataset Mappings</h2>
            <p className="mt-0.5 text-[13px] text-slate-500">Provider and merchant fee configurations</p>
          </div>
          <button
            onClick={() => setShowGenerateQuote(true)}
            className="btn-primary"
          >
            <Plus className="w-4 h-4" />
            New Mapping
          </button>
        </div>

        {loadingMappings ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : datasetMappings.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-[14px] text-slate-500">No mappings yet. Create one to configure provider and merchant fees.</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {datasetMappings.map((mapping) => (
              <div
                key={mapping.id}
                className={`px-5 py-4 hover:bg-slate-50 transition-colors ${mapping.archived ? 'opacity-50' : ''}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-[14px] font-semibold text-slate-900 truncate">{mapping.name}</h3>
                      {mapping.archived && <span className="badge-gray">Archived</span>}
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[12px] text-slate-400">
                      <span>{mapping.merchant?.name || 'Unknown merchant'}</span>
                      <span>&middot;</span>
                      <span>{Object.keys(mapping.bank_mappings).length} provider{Object.keys(mapping.bank_mappings).length !== 1 ? 's' : ''}</span>
                      <span>&middot;</span>
                      <span>{new Date(mapping.created_at).toLocaleDateString()}</span>
                    </div>
                    {Object.keys(mapping.bank_mappings).length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {Object.entries(mapping.bank_mappings).map(([datasetName, bank]) => (
                          <span key={datasetName} className="badge-violet">
                            {datasetName} → {bank.bank_alias || bank.bank_name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-0.5 shrink-0">
                    <button
                      onClick={() => navigate(`/datasets/mappings/${mapping.id}/edit`)}
                      className="btn-ghost"
                      title="Edit"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDuplicateMapping(mapping)}
                      disabled={duplicatingMapping === mapping.id}
                      className="btn-ghost"
                      title="Duplicate"
                    >
                      {duplicatingMapping === mapping.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </button>
                    <button
                      onClick={() => handleToggleArchiveMapping(mapping.id, !mapping.archived)}
                      disabled={archivingMapping === mapping.id}
                      className="btn-ghost"
                      title={mapping.archived ? 'Restore' : 'Archive'}
                    >
                      {archivingMapping === mapping.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Archive className="w-4 h-4" />
                      )}
                    </button>
                    <button
                      onClick={() => setMappingToDelete(mapping)}
                      className="btn-ghost hover:!text-red-500 hover:!bg-red-50"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showGenerateQuote && (
        <GenerateDatasetMappingModal
          datasetId={datasetId}
          datasetName={dataset.name}
          bankMappings={bankMappings}
          onClose={() => {
            setShowGenerateQuote(false);
            loadDatasetMappings();
          }}
        />
      )}

      {mappingToDelete && (
        <DeleteConfirmationModal
          title="Delete Mapping"
          message={`Are you sure you want to delete "${mappingToDelete.name}"? This action cannot be undone.`}
          onConfirm={handleDeleteMapping}
          onCancel={() => setMappingToDelete(null)}
          isDeleting={deletingMapping}
        />
      )}
    </div>
  );
}
